/**
 * `/verify-email?token=…` — where the link in the confirmation email lands.
 *
 * The user did not choose to come here and has nothing to fill in, so the page
 * does the work on arrival and gets out of the way. Verifying also signs them
 * in (the worker sets a session cookie), which is why success navigates
 * straight to the dashboard rather than asking for the password they chose two
 * minutes ago.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useSession } from "../../auth/AuthProvider";
import { AuthError } from "../../auth/authApi";
import { AuthCard } from "./AuthCard";

export function VerifyEmail() {
  const { api, refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [error, setError] = useState<string | null>(null);

  // A verification token is single-use, and React's development StrictMode runs
  // effects twice. Without this guard the second run spends a token the first
  // already consumed, turning a working link into an expired one on the user's
  // only click.
  //
  // There is deliberately no `active`/cleanup flag alongside it. The obvious
  // pairing — set `active = false` on cleanup, check it before navigating —
  // deadlocks here: StrictMode's cleanup cancels the first attempt, the second
  // run returns early because the guard is already set, and the page sits on
  // "One moment…" forever with the email verified and nothing to show for it.
  // The guard is what makes at most one request; nothing else needs to.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setError("That link is missing its confirmation code. Copy the whole link from the email, or sign in to have another sent.");
      return;
    }

    void (async () => {
      try {
        await api.verifyEmail(token);
        await refresh();
        navigate("/dashboard", { replace: true });
      } catch (cause) {
        setError(explain(cause));
      }
    })();
  }, [api, navigate, refresh, token]);

  if (!error) {
    return (
      <AuthCard title="Confirming your email">
        <p className="muted">One moment…</p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="That link didn't work"
      error={error}
      footer={
        <>
          Try to <Link to="/login">sign in</Link> — we'll send a fresh link if the address still
          needs confirming.
        </>
      }
    >
      <p className="muted">
        Confirmation links expire an hour after they're sent, and each one can only be used once.
      </p>
    </AuthCard>
  );
}

function explain(cause: unknown): string {
  if (!(cause instanceof AuthError)) {
    return "Could not reach the server. Check your connection and try the link again.";
  }
  if (cause.code === "TOKEN_EXPIRED") {
    return "That confirmation link has expired.";
  }
  if (cause.code === "INVALID_TOKEN") {
    return "That confirmation link is not valid — it may already have been used.";
  }
  return cause.message;
}
